import { Component, Prop, Vue, toNative } from "vue-facing-decorator";
import { MiaPlaza } from "../../../../Reinforced.Typings";
import { type ForNotifier } from "./DerivedInterfaces";
import IdGenerator from "@/utils/IdGenerator";
import Banner from "../Banner.vue";
import CurriculumInitialized from "../CurriculumInitialized.vue";
import MiaPrepAnnouncement from "../MiaPrepAnnouncement.vue";
import Prompt from "../Prompt.vue";
import SessionMessage from "../SessionMessage.vue";
import SiteSlowWarning from "../SiteSlowWarning.vue";
import ValidationError from "../ValidationError.vue";
import IntroductionTutorialVideos from "../IntroductionTutorialVideos.vue";
import { AkiMessage, MemberPenalty, MultiplayerGameInvitation } from "@/chunks/Students";

const components = {
	AkiMessage,
	Banner,
	CurriculumInitialized,
	MemberPenalty,
	MiaPrepAnnouncement,
	MultiplayerGameInvitation,
	Prompt,
	SessionMessage,
	SiteSlowWarning,
	ValidationError,
	IntroductionTutorialVideos,
};

@Component({
	components,
	template: `<span>
					<vue-snotify/>
					<component v-for="item in notifications" :key="item.UniqueIdentifier" :is="item.Template" :from="item" />
				</span>`,
})
export class Notifier extends Vue {
	// A singleton so we can show notifications from anywhere in our JS/TS code.
	public static Instance: Notifier;

	public static show<T extends MiaPlaza.Control.Vue.Notifications.Shared.INotification>(notification: ForNotifier<T>): string {
		return Notifier.Instance.show(notification);
	}

	public static hide(uniqueIdentifier: string): void {
		return Notifier.Instance.hide(uniqueIdentifier);
	}

	@Prop({ required: true, type: Array }) protected initialNotifications!:
	MiaPlaza.Control.Vue.Notifications.Shared.INotification[];

	private notifications: MiaPlaza.Control.Vue.Notifications.Shared.INotification[] = [];

	public hide(uniqueIdentifier: string): void {
		this.notifications = this.notifications.filter((n) => n.UniqueIdentifier !== uniqueIdentifier);
	}

	public show<T extends MiaPlaza.Control.Vue.Notifications.Shared.INotification>(notification: ForNotifier<T>): string {
		if (this.hasReachedMaximumNotificationAmount()) {
			return "";
		}

		const notificationWithDefaults = {
			Lifetime: MiaPlaza.Model.Lifetime.VOLATILE,
			UniqueIdentifier: IdGenerator.Generate(),
			...notification,
		};

		if (!(notification.Template in components)) {
			throw Error(`Unsupported notification type ${notification.Template}`);
		}

		this.notifications.push(notificationWithDefaults);

		return notificationWithDefaults.UniqueIdentifier;
	}

	protected created(): void {
		this.notifications = [];
		this.showNotifications();
		Notifier.Instance = this;
	}

	private showNotifications() {
		this.initialNotifications.forEach((notification) => this.show(notification));
	}

	private hasReachedMaximumNotificationAmount(): boolean {
		const maxNotificationAmount = this.$snotify.config.global?.maxOnScreen ?? 0;
		return this.$snotify.notifications.length >= maxNotificationAmount;
	}
}

export default toNative(Notifier);
